import { useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';

import { ReactCreatableSelect } from 'dashboard/components/ReactSelect';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { Tag, TagScope } from 'dashboard/models/tag';
import { showMessage } from 'dashboard/redux/actions/messages';
import { RootState } from 'dashboard/redux/store';
import { loadTags } from 'dashboard/redux/thunk-actions/tags';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiLoader from 'lib/hooks/useApiLoader';
import { ApiModel } from 'lib/interfaces/api';

export interface ConnectedTagSelectProps {
  id?: string;
  inputId?: string;
  className?: string;
  name?: string;
  value: string[];
  tagScope: TagScope;
  placeholder?: string;
  hasError?: boolean;
  disabled?: boolean;
  size?: 'md' | 'sm';
  returnName?: boolean;
  onChange: (values: string[]) => void;
}

const ConnectedTagSelect = ({
  id,
  inputId,
  className,
  name,
  value,
  placeholder,
  hasError,
  disabled,
  tagScope,
  onChange,
  tags,
  returnName,
  size = 'md',
  ...props
}: ConnectedTagSelectProps & PropsFromRedux) => {
  const [newTags, setNewTags] = useState([]);

  const options = tags[tagScope].concat(newTags).map((tag) => {
    return { label: tag.attributes.name, value: returnName ? tag.attributes.name : tag.id };
  });

  const { callback: loadTags, loading: isLoading } = useApiLoader({
    loader: () => props.loadTags(tagScope),
    deps: [props.loadTags],
    onError: () => props.showMessage('Failed to load tags.', MessageType.Failure),
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const filteredValue = useMemo(() => {
    if (isLoading) {
      return value;
    }

    return value.filter((singleValue) => options.some((option) => option.value === singleValue));
  }, [isLoading, value, options]);

  useEffect(() => {
    if (filteredValue.length !== value.length) {
      onChange(filteredValue);
    }
  }, [filteredValue, value, onChange]);

  const { callback: addNewTag, loading: isLoadingNewTag } = useApiCallback(
    async (name: string) => {
      const newTag = await SessionClientDashboard.postResourceCreate<Tag>({
        attributes: { name },
        type: ApiModel.Tag,
      });

      // Note we intentionally do not reload tags here. This is to ensure that
      // for a given loaded ConnectedTagSelect instance, the experience is consistent
      // (i.e. if another person creates a new tag, it doesn't magically show up
      // after you added a new tag).
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      setNewTags(newTags.concat(newTag.data));
      onChange((value ?? []).concat([returnName ? newTag.data.attributes.name : newTag.data.id]));
    },
    [loadTags, onChange],
  );

  return (
    <ReactCreatableSelect
      size={size}
      id={id}
      inputId={inputId}
      className={className}
      name={name}
      value={value}
      placeholder={placeholder}
      hasError={hasError}
      disabled={disabled || isLoading || isLoadingNewTag}
      isMulti={true}
      isSearchable={true}
      portal={true}
      options={options}
      onChange={onChange}
      onCreateOption={addNewTag}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    tags: state.tags.tags,
  };
};

const mapDispatchToProps = {
  loadTags,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(ConnectedTagSelect);
